
import Navigator from './Navigator';
import { useCallback, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider } from '@material-ui/core';
import { materialUITheme } from 'Theme/MaterialUITheme';
// import { firebaseAnalytics } from 'firebase.config';
import { CacheBuster, DialogSecurityImage, CookieConsentDialog, CookieConsentBanner, FloatingCookieButton } from 'Components';
import FloatingAccessibilityButton from 'Components/Accessibility/floating-accessibility-button';
import { useCookieConsent } from 'Hooks/useCookieConsent';
import { useDispatch, useSelector } from 'react-redux';
import { onInıtDataRequest, storeApp, storeHasAccess } from 'Stores/App';
import { generateLanguageParam, getEnvironment, initMasterpassSdk } from 'Utils';
import { history } from 'Stores';

import 'react-toastify/dist/ReactToastify.css';

const isDev = getEnvironment();

const COOKIE_EXCLUDED_ROUTES = [
  '/mobile-add-credit-card',
  '/mobile-payment',
  '/mobile-account-otp',
  '/mobile-masterpass-connect',
  '/mobilePaymentOrder',
  '/mobile-add-credit-card-plus-card-application',
  '/mini-app',
  '/microcredit',
  '/fibaBankSuccessPage',
  '/fibaBankFailPage',
  '/fibabankDocumentApprove',
];

const isCookieExcludedRoute = (pathname: string): boolean => {
  return COOKIE_EXCLUDED_ROUTES.some(route => 
    pathname.toLowerCase().startsWith(route.toLowerCase())
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
});


const MOBILE_BREAKPOINT = 768;


const checkIsMobileView = (): boolean => {
 
  const isSmallScreen = window.innerWidth < MOBILE_BREAKPOINT;
  
 
  const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
  
  return isSmallScreen || isMobileUserAgent;
};

const App = () => {
  const dispatch = useDispatch();
  const hasAccess = useSelector(storeHasAccess);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [isMobileView, setIsMobileView] = useState(checkIsMobileView());
  const {
    showBanner,
    showDetailedDialog,
    openDetailedDialog,
    closeDetailedDialog,
    acceptAll,
    rejectAll,
    preferences,
    savePreferences,
  } = useCookieConsent();

  const { cacheLoading, isLatestVersion } = useSelector(storeApp);

  useEffect(() => {
    const unlisten = history.listen((location) => {
      setCurrentPath(location.pathname);
    });
    return () => unlisten();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(checkIsMobileView());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isExcludedRoute = isCookieExcludedRoute(currentPath);
  const shouldShowCookies = !(isMobileView && isExcludedRoute);

  const refreshCacheAndReload = useCallback(async () => {
    if (caches) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
    window.location.reload();
  }, []);

  // Masterpass SDK'sının oturumdan bağımsız kurulumu: servis kökü ve dil.
  // ⚠️ Eski `setClientId` + `setAddress` çağrılarının yerini alır; üye işyeri
  // kimliği artık burada değil, oturum açılınca JWT'den kuruluyor
  // (bkz. `authorizeMasterpassSdk`).
  useEffect(() => {
    initMasterpassSdk();
  }, []);

  useEffect(() => {
    if (!cacheLoading && !isLatestVersion) {
      refreshCacheAndReload();
    }
  }, [isLatestVersion, cacheLoading, refreshCacheAndReload]);

  useEffect(() => {
    dispatch(onInıtDataRequest(generateLanguageParam()));
    // firebaseAnalytics();
    /* window.EvamSdkObject && window.EvamSdkObject.consentAccepted(); */
  }, []);

  return (
    <CacheBuster>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={materialUITheme}>
          <Navigator />
          <DialogSecurityImage />
          {/* Erişilebilirlik butonu çerez bileşenleriyle aynı kapıdan geçer:
              mobil görünüm + webview route ise ikisi de gizlenir. */}
          {shouldShowCookies && <FloatingAccessibilityButton />}
          {shouldShowCookies && (
            <>
              <CookieConsentBanner
                isVisible={showBanner}
                onCustomize={openDetailedDialog}
                onAcceptAll={acceptAll}
                onRejectAll={rejectAll}
                onSavePreferences={savePreferences}
              />
              <CookieConsentDialog
                open={showDetailedDialog}
                preferences={preferences}
                onClose={closeDetailedDialog}
                onAcceptAll={acceptAll}
                onRejectAll={rejectAll}
                onSavePreferences={savePreferences}
              />
              {/* Bölüm 7: rıza her zaman geri alınabilsin diye kalıcı giriş noktası.
                  İlk ziyarette banner zaten erişim sağladığından, seçim yapıldıktan sonra gösterilir. */}
              {!showBanner && <FloatingCookieButton onClick={openDetailedDialog} />}
            </>
          )}
        </ThemeProvider>
      </QueryClientProvider>
    </CacheBuster>
  );
};

export default App;